import React, { useCallback, useEffect, useMemo, useState } from "react";
import "ace-builds";
import AceEditor from "react-ace";
import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";

import "ace-builds/src-noconflict/mode-mysql";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-xcode";
import "ace-builds/src-noconflict/ext-searchbox";
import "ace-builds/src-noconflict/ext-language_tools";

import ConfirmPopup from "./ConfirmPopup";
import ResultTabs from "../MyWorkspace/DataExplorer/ResultTabs/Index";
import ButtonUI from "./ButtonUI";
import WorkspaceService from "../../service/workspace";
import { useParams } from "react-router-dom";
import SQLService from "../../service/mysqlService";
import Salesforce from "../../service/salesforce";

import {
  checkIfSelectQuery,
  getTabQuery,
  handleQueryResponse,
  removeQueryComments,
  setPrevState,
} from "../../util/common";
import { useSelector } from "react-redux";
import QueryDialog from "./QueryDialog";

const useStyles = makeStyles()((theme) => ({
  ACE_EDITOR: {
    fontSize: "0.7rem",
    "&::selection": { background: "#B9B9B9", color: "#000000" },
    "&::-moz-selection": { background: "#B9B9B9", color: "#000000" },
    "&::-webkit-selection": { background: "#B9B9B9", color: "#000000" },

    "& .ace_scrollbar-v": {
      width: "6px !important",
      backgroundColor: "transparent",
    },
    "& .ace_scrollbar-h": {
      height: "6px !important",
      backgroundColor: "transparent",
    },
  },
}));

const MyAceEditor = React.forwardRef((props, ref) => {
  const { Workspace_id, Job_id } = useParams();

  const { classes } = useStyles();

  const editJob = useSelector((state) => state?.dataPipeline ?? {});

  const {
    value,
    setQuery,
    type,
    runParams = {},
    resultFromPopup,
    queryLastUpdatedByName,
    onError = () => {},
    fromPopup = false,
    location = "SNOWFLAKE",
    queryRestriction = {},
  } = props;

  const { onlyOneQuery = true, onlySelectQuery = true } = queryRestriction;

  const [queryPopup, setQueryPopup] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, query: "" });
  const [error, setError] = useState("");

  const [cursorPosition, setCursorPosition] = useState(0);

  const [queryResultState, setQueryResultState] = useState({
    id: 1,
    queryResultLoding: false,
    queryResultColumns: [],
    queryResult: [],
    queryError: "",
  });

  const refreshState = useCallback(() => {
    setQueryResultState({ id: 1, queryResultLoding: false, queryResultColumns: [], queryResult: [], queryError: "" });
    setCursorPosition(0);
    setConfirm({ open: false, query: "" });
  }, []);

  const jobIdPresent = useMemo(
    () => Boolean(Job_id ?? parseInt(editJob?.editJob?.id ?? 0)),
    [Job_id, editJob?.editJob?.id]
  );

  const isValidationNeeded = useMemo(
    () => onlyOneQuery || onlySelectQuery || runParams.refreshParam,
    [onlyOneQuery, onlySelectQuery, runParams.refreshParam]
  );

  const allowQueryRun = useMemo(
    () => !!setQuery && ["snowflake", "mysql", "salesforce"].includes(type?.toLowerCase()),
    [setQuery, type]
  );

  const showResult = useMemo(
    () =>
      queryResultState.queryResultLoding ||
      queryResultState.queryResultColumns?.length !== 0 ||
      queryResultState.queryError !== "",
    [queryResultState.queryError, queryResultState.queryResultColumns?.length, queryResultState.queryResultLoding]
  );

  const handleQueryPopupOpen = (flag) => {
    setQueryPopup(flag ?? false);
  };

  const handleQueryValidation = useCallback(
    (value) => {
      // Remove carriage return and comment
      const trimmedValue = removeQueryComments(value);
      const queries = trimmedValue.split(";");

      const regex = /^(WITH\s+[\s\S]+?\s+SELECT\s+[\s\S]+?;?|SELECT\s+[\s\S]+?;?)$/is;
      let error = "";

      if (trimmedValue) {
        if (onlyOneQuery && (queries.length > 2 || (queries[1] && queries[1].trim() !== ""))) {
          error = "Please enter only 1 query.";
        } else if (onlySelectQuery && !regex.test(queries[0])) {
          error = "Please enter only SELECT query.";
        }
      }

      setError(error);
      onError(error);
      if (typeof setQuery === "function") {
        setQuery(value);
      }
    },
    [onError, onlyOneQuery, onlySelectQuery, setQuery]
  );

  const handleCursorChange = useCallback(
    (selection) => {
      if (selection?.cursor?.row !== cursorPosition) setCursorPosition(selection.cursor.row);
    },
    [cursorPosition]
  );

  const handleClose = useCallback(() => {
    if (isValidationNeeded) handleQueryValidation(value);
    handleQueryPopupOpen(false);
  }, [handleQueryValidation, isValidationNeeded, value]);

  const runQuery = useCallback(
    async (tabQuery = "") => {
      setPrevState(setConfirm, { open: false, query: "" });
      setPrevState(setQueryResultState, { queryResultLoding: true });

      let response = null;
      if (type === "snowflake") {
        const workspace = new WorkspaceService();
        response = await workspace.runSnowflakeQuery(Workspace_id, location, { query: tabQuery, ...runParams });
      } else if (type === "mysql") {
        const sqlService = new SQLService();
        response = await sqlService.getMysqlResult(Workspace_id, location, {
          query: tabQuery,
        });
      } else if (type === "salesforce") {
        const salesforce = new Salesforce();
        response = await salesforce.runQuery(Workspace_id, location, {
          query: removeQueryComments(tabQuery),
          limit: 100,
          preview: true,
        });
      }

      if (response) {
        handleQueryResponse(response, setQueryResultState);
        // For Popup
        if (fromPopup && props.onQueryRunFromPopup) {
          props.onQueryRunFromPopup(response);
        }
      }

      // Closing popup
      setConfirm({ open: false, loading: false, query: "" });
      setPrevState(setQueryResultState, { queryResultLoding: false });
    },
    [type, fromPopup, props, Workspace_id, location, runParams]
  );

  const getQueryResult = useCallback(
    async (queryRef) => {
      const tabQuery = getTabQuery({ query: value, queryRef, cursorPosition });

      const isSelectQuery = checkIfSelectQuery(tabQuery);
      if (isSelectQuery) await runQuery(tabQuery);
      else setPrevState(setConfirm, { open: true, query: tabQuery });
    },
    [cursorPosition, runQuery, value]
  );

  useEffect(() => {
    refreshState();
    return () => {};
  }, [location, refreshState, runParams.refreshParam]);

  useEffect(() => {
    if (resultFromPopup) setQueryResultState(resultFromPopup);
  }, [resultFromPopup]);

  useEffect(() => {
    // For first load only
    if ((fromPopup && isValidationNeeded) || runParams.refreshParam) {
      handleQueryValidation(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPopup, isValidationNeeded, runParams.refreshParam]);

  return (
    <>
      <AceEditor
        mode="mysql"
        theme="xcode"
        fontFamily={("Roboto", "Helvetica", "Arial", "sans-serif")}
        required
        variant="outlined"
        showPrintMargin={false}
        setOptions={{
          useWorker: false,
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: true,
          enableSnippets: false,
          showLineNumbers: true,
          tabSize: 4,
        }}
        ref={ref}
        autofocus
        // From below do not change the order unless required
        onChange={(value) => handleQueryValidation(value)}
        onCursorChange={handleCursorChange}
        {...props}
        className={`${classes.ACE_EDITOR} ${props.className || ""}`}
        style={{ border: !!error ? "1px solid red" : "1px solid", paddingTop: "10px", ...(props?.style || {}) }}
      />

      {allowQueryRun && (
        <>
          <Box component="span" color={(theme) => theme.palette.error.main}>
            {error}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            {!fromPopup && (
              <Tooltip arrow title="Open in Full Screen" disableFocusListener placement="top-start">
                <IconButton onClick={() => handleQueryPopupOpen(true)}>
                  <ZoomOutMapIcon sx={{ color: "#0000009" }} />
                </IconButton>
              </Tooltip>
            )}

            <ButtonUI
              variant="contained"
              color="success"
              onClick={() => getQueryResult(ref)}
              disabled={queryResultState.queryResultLoding || value?.trim() === ""}
              sx={{ ml: 3 }}
            >
              Run Query
            </ButtonUI>
          </Box>
        </>
      )}
      {showResult && (
        <Box sx={{ mt: 2, border: "1px solid" }}>
          <ResultTabs
            queryResultState={queryResultState}
            external={true}
            height={queryResultState.queryError ? 200 : 300}
          />
        </Box>
      )}

      <>
        {confirm.open && (
          <ConfirmPopup
            configData={{
              popupTitle: "Run Query",
              popupMessage: (
                <Box sx={{ m: 0, p: 0 }}>
                  <Typography>Warning: Running this query may alter the data or table.</Typography>
                  <Typography>Are you sure you want to run the query?</Typography>
                </Box>
              ),
              handleCancel: () => setConfirm({ open: false, loading: false, query: "" }),
              handleClick: () => runQuery(confirm.query),
              loadingForConfirm: false,
            }}
          />
        )}

        {!fromPopup && queryPopup && (
          <QueryDialog
            value={value}
            setQueryFromPopup={(value) => setQuery(value)}
            handleClose={handleClose}
            loading={queryPopup}
            isOpen={queryPopup}
            queryLastUpdatedByName={queryLastUpdatedByName && jobIdPresent ? queryLastUpdatedByName : ""}
            location={location}
            type={type}
            runParams={runParams}
            // For Popup to orignal
            queryResultState={queryResultState}
            onQueryRunFromPopup={(response) => handleQueryResponse(response, setQueryResultState)}
            queryRestriction={queryRestriction}
          />
        )}
      </>
    </>
  );
});

export default React.memo(MyAceEditor);
